// Route 1 — Our Services: hierarchical IA (parent groups -> offence pages)

export const SERVICE_GROUPS = [
  {
    slug: "general-crime",
    title: "General Crime",
    icon: "Scale",
    tagline:
      "Expert defence for every criminal matter — from the police station through to trial.",
    summary:
      "We are experts in criminal defence work representing those accused of every type of criminal matter. The depth and breadth of experience we have at the practice will ensure you have the highest quality representation from your first contact with us through to the conclusion of your matter.",
    children: [
      {
        slug: "assault-violent-offences",
        title: "Assault & Violent Offences",
        icon: "ShieldAlert",
        tagline: "Defence for common assault, ABH, GBH, wounding with intent and affray.",
        summary:
          "Allegations of violence range from common assault to wounding with intent under section 18 of the Offences Against the Person Act 1861. The consequences of conviction can be severe, and the issues — self-defence, identification, intent — demand meticulous preparation. We defend clients at every level of seriousness, combining excellent judgment with an unrivalled tenacity in defence of our clients.",
        covers: [
          "Common assault & battery",
          "Assault occasioning actual bodily harm (ABH)",
          "Grievous bodily harm (GBH) — sections 18 & 20",
          "Affray & threatening behaviour",
          "Self-defence and defence of another",
        ],
        faqs: [
          {
            q: "I acted in self-defence — does that help me?",
            a: "Self-defence is a complete defence where you genuinely feared for your safety and acted reasonably and proportionately. Building that defence starts with what you say (and don't say) at the police station — call us before your interview.",
          },
          {
            q: "What is the difference between ABH and GBH?",
            a: "ABH involves injury that is more than transient or trifling; GBH means really serious harm. Section 18 GBH (with intent) carries a maximum of life imprisonment, which is why early specialist advice is essential.",
          },
        ],
      },
      {
        slug: "drug-offences",
        title: "Drug Offences",
        icon: "FlaskConical",
        tagline: "Defence for possession, supply, production and large-scale drug conspiracies.",
        summary:
          "From simple possession to large-scale drug conspiracies, we have defended clients across the full spectrum of drug offences — including multiple individuals charged with wide scale cocaine supply in the EncroChat cases. These prosecutions often turn on complex evidence: telephone data, cell-site analysis, encrypted communications and expert valuation. We break that evidence down and test every link in the prosecution case.",
        covers: [
          "Possession & possession with intent to supply",
          "Supply and offering to supply",
          "Production and cultivation",
          "Conspiracy & large-scale drug operations",
          "EncroChat and encrypted communications cases",
        ],
        faqs: [
          {
            q: "I've been charged with intent to supply but the drugs were for personal use.",
            a: "Quantity, packaging, messages and cash are all relied on to infer intent — and all can be challenged. The difference between possession and supply is enormous at sentence, so the issue must be confronted early and robustly.",
          },
          {
            q: "Do you have experience with EncroChat prosecutions?",
            a: "Yes. We have represented multiple individuals charged with wide scale cocaine supply arising from the EncroChat operation, and we understand the technical and legal challenges these cases raise.",
          },
        ],
      },
      {
        slug: "public-order-offences",
        title: "Public Order & Related Offences",
        icon: "Users",
        tagline: "Defence for affray, violent disorder, harassment and offensive weapons.",
        summary:
          "Public order allegations often arise from fast-moving, confused incidents involving many people — and identification, participation and intent are frequently in dispute. We also defend offences relating to offensive weapons and controlling and coercive behaviour, an area in which our team writes and advises extensively.",
        covers: [
          "Affray & violent disorder",
          "Harassment & stalking",
          "Controlling and coercive behaviour",
          "Offensive weapons & bladed articles",
          "Protest-related offences",
        ],
        faqs: [
          {
            q: "I was present but didn't take part — am I still liable?",
            a: "Mere presence is not enough for guilt, but prosecutors often rely on joint enterprise principles. Precisely what you did, said and intended matters — we will scrutinise the footage and witness accounts frame by frame.",
          },
          {
            q: "What is controlling and coercive behaviour?",
            a: "An offence under the Serious Crime Act 2015 covering patterns of controlling behaviour within relationships. These allegations are sensitive and evidence-heavy — see our detailed guide in Legal Resources, and speak to us before any interview.",
          },
        ],
      },
      {
        slug: "motoring-road-traffic",
        title: "Motoring & Road Traffic",
        icon: "Car",
        tagline:
          "Defence for drink driving, speeding, careless and dangerous driving, and death by dangerous driving.",
        summary:
          "Our solicitors have extensive experience in assisting clients who face prosecution for driving offences under the road traffic legislation, an area of law which is increasingly complex and far-reaching. A conviction can affect your livelihood, insurance and reputation — we assess every case for procedural and evidential weaknesses.",
        covers: [
          "Drink driving & drug driving",
          "Speeding & totting-up bans",
          "Driving without a licence or insurance",
          "Careless & dangerous driving",
          "Death by dangerous driving",
        ],
        faqs: [
          {
            q: "Will I lose my licence?",
            a: "Not necessarily. Penalties vary widely and there are often arguments to be made — from exceptional hardship to challenging the evidence itself. Early advice gives you the best prospect of keeping your licence.",
          },
          {
            q: "Is it worth instructing a solicitor for a motoring matter?",
            a: "Road traffic law is increasingly complex and far-reaching. A conviction can affect your livelihood, insurance and reputation. We assess every case for procedural and evidential weaknesses.",
          },
        ],
      },
      {
        slug: "sexual-offences",
        title: "Sexual Offences",
        icon: "FileWarning",
        tagline: "Discreet, expert defence for rape, sexual assault and related allegations.",
        summary:
          "An allegation of a sexual offence is devastating — for your reputation, your family and your liberty. These cases turn on issues of consent, credibility and increasingly on digital evidence. We have secured acquittals in the most serious cases, including rape trials at the Crown Court, and we handle every case with complete discretion and empathy.",
        covers: [
          "Rape & assault by penetration",
          "Sexual assault",
          "Indecent images allegations",
          "Historic allegations",
          "Consent and capacity issues",
        ],
        faqs: [
          {
            q: "The allegation is historic — can it still be prosecuted?",
            a: "Yes. Indictable offences have no time limit, and investigations may begin decades after the events alleged. Such cases raise particular issues — fading memories, lost evidence — which an experienced defence team can use to test the reliability of the prosecution case.",
          },
          {
            q: "Will my case stay confidential?",
            a: "We pride ourselves on our low profile approach. We act for high profile individuals and professionals, and absolute discretion is fundamental to how we work.",
          },
        ],
      },
      {
        slug: "homicide",
        title: "Homicide — Murder & Manslaughter",
        icon: "Gavel",
        tagline: "Defence in murder and manslaughter cases at the Central Criminal Court and beyond.",
        summary:
          "Murder carries a mandatory sentence of life imprisonment and perhaps the greatest stigma of any crime. Our team has defended in the most serious homicide cases — including four-handed murder trials at the Central Criminal Court. From the first police interview to trial, every decision matters: causation, intent, self-defence and the partial defences of diminished responsibility and loss of control all demand specialist expertise.",
        covers: [
          "Murder",
          "Manslaughter — unlawful act & gross negligence",
          "Diminished responsibility & loss of control",
          "Attempted murder",
          "Causation and intent disputes",
        ],
        faqs: [
          {
            q: "What is the sentence for murder?",
            a: "The sentence is mandatory life imprisonment, with the court setting a minimum term under Schedule 21 of the Sentencing Act 2020 — starting points range from 15 years to a whole life order. Read our full guide in Legal Resources.",
          },
          {
            q: "What are the partial defences to murder?",
            a: "Diminished responsibility, loss of control, and killing in pursuit of a suicide pact. When successfully relied upon they result in a conviction for manslaughter rather than murder — a critical difference at sentence.",
          },
        ],
      },
    ],
  },
  {
    slug: "serious-specialist-crime",
    title: "Serious & Specialist Crime",
    icon: "Briefcase",
    tagline:
      "Market-leading defence in serious fraud, financial crime and cross-border proceedings.",
    summary:
      "We are specialists in defending serious and complex crime and fraud — a member of the Specialist Fraud Panel since its inception. These cases are often complex and paper heavy; we break them down to aid understanding and preparation, representing individuals and companies in some of the most serious cases in the country.",
    children: [
      {
        slug: "serious-fraud",
        title: "Serious Fraud & Financial Crime",
        icon: "Banknote",
        tagline: "Defence for fraud, money laundering and complex financial crime.",
        summary:
          "Having been a member of the Specialist Fraud Panel since its inception, we have the depth and breadth of experience to advise you on investigations into fraud and other types of financial crime. These cases are often complex and paper heavy and we break them down to aid in understanding and preparation for these allegations — including the first prosecution under the Russia sanctions regime, in which our client was acquitted.",
        covers: [
          "Serious and complex fraud",
          "Money laundering",
          "Sanctions breaches",
          "Investigations by the SFO, HMRC and FCA",
          "Cryptocurrency-related allegations",
        ],
        faqs: [
          {
            q: "I am under investigation but have not been charged. Should I get advice now?",
            a: "Yes — early advice is critical in fraud matters. How an interview under caution is handled can shape the entire case. We regularly secure outcomes where charges are never brought.",
          },
          {
            q: "These cases involve huge amounts of paperwork. How do you manage that?",
            a: "Fraud cases are often paper heavy. We break them down methodically so you understand exactly what is alleged, and we prepare a defence strategy built on complete command of the evidence.",
          },
        ],
      },
      {
        slug: "corporate-crime",
        title: "Corporate Crime",
        icon: "Building2",
        tagline:
          "Defence for bribery, corruption, fraud, directors' offences and phoenixing companies.",
        summary:
          "Offences committed relating to companies both by individuals and the corporate entity are becoming more common and the law an ever changing landscape. Our experience extends to representing both companies and their directors — including securing the acquittal of an individual charged with bribery and money laundering following trial at Southwark Crown Court.",
        covers: [
          "Bribery & corruption",
          "Corporate fraud",
          "Directors' offences",
          "Phoenixing companies",
          "Regulatory investigations",
        ],
        faqs: [
          {
            q: "Can you act for the company as well as its directors?",
            a: "Yes. Our experience extends to representing both corporate entities and individual directors, and we will advise on conflicts at the outset.",
          },
          {
            q: "The company has received a request for information — what now?",
            a: "Do not respond before taking advice. The early stages of a corporate investigation often determine its outcome. We can manage the response and engage with investigators on your behalf.",
          },
        ],
      },
      {
        slug: "proceeds-of-crime",
        title: "Proceeds of Crime & Restraint",
        icon: "Lock",
        tagline:
          "Defence in confiscation, restraint, cash seizure and account freezing proceedings.",
        summary:
          "Proceedings under the Proceeds of Crime Act 2002 can be as devastating as the criminal case itself — freezing assets, seizing cash and pursuing confiscation long after sentence. These are complex, technical proceedings in which the burden frequently shifts to the defence. We act in restraint, confiscation, cash forfeiture and account freezing order proceedings, protecting your assets and your position.",
        covers: [
          "Confiscation proceedings under POCA",
          "Restraint orders",
          "Police cash seizure & forfeiture",
          "Account freezing orders",
          "Enforcement & certificate of inadequacy applications",
        ],
        faqs: [
          {
            q: "The police have seized my cash — can I get it back?",
            a: "The police can detain seized cash only with court authority, and you are entitled to contest both detention and forfeiture. Strict time limits apply, so act quickly — see our Police Cash Seizure guide in Legal Resources.",
          },
          {
            q: "What is a restraint order?",
            a: "A restraint order freezes your assets pending investigation or confiscation. It can be varied or discharged in the right circumstances — including to release reasonable living and legal expenses.",
          },
        ],
      },
      {
        slug: "extradition",
        title: "Extradition",
        icon: "Globe2",
        tagline: "Defence for individuals facing extradition proceedings.",
        summary:
          "Extradition is the official process whereby one country transfers an individual who is accused or convicted of a criminal offence to another country. This may be requested to enable the requesting country to try the individual on outstanding criminal charges or to ensure that they serve their sentence (if convicted). It is a highly technical area with numerous statutory bars and human rights protections.",
        covers: [
          "Extradition requests & arrest warrants",
          "Proceedings at Westminster Magistrates' Court",
          "High Court appeals",
          "Interpol Red Notice advice",
        ],
        faqs: [
          {
            q: "I have been arrested on an extradition warrant. What happens next?",
            a: "You will be brought before Westminster Magistrates' Court. Bars to extradition may apply — including human rights grounds, passage of time and forum. Contact us immediately so we can act before the initial hearing.",
          },
          {
            q: "Can extradition be challenged?",
            a: "Yes. Extradition is a highly technical area with numerous statutory bars and human rights protections. We will assess every available challenge from the outset.",
          },
        ],
      },
    ],
  },
  {
    slug: "regulatory-investigations",
    title: "Regulatory & Investigations",
    icon: "Landmark",
    tagline:
      "Robust representation in regulatory investigations, inquests and public inquiries.",
    summary:
      "From Trading Standards and the Health and Safety Executive to professional regulators and public inquiries, we provide practical and robust advice at every stage of an investigation — protecting your position, your livelihood and your reputation.",
    children: [
      {
        slug: "trading-standards",
        title: "Trading Standards",
        icon: "ShieldCheck",
        tagline: "Defence for businesses facing Trading Standards investigations and prosecutions.",
        summary:
          "Investigations by Trading Standards for breaches of their Regulations can be a worrying time for a business. We are on hand to offer practical and robust advice and representation in responding to a request for information and interview.",
        covers: [
          "Responses to requests for information",
          "Interviews under caution",
          "Consumer protection prosecutions",
          "Product safety & labelling allegations",
        ],
        faqs: [
          {
            q: "Trading Standards have asked my business for information. Must I respond?",
            a: "Often yes — but how you respond matters enormously. We will advise on your obligations and manage the response to protect your position from the outset.",
          },
        ],
      },
      {
        slug: "health-safety-investigations",
        title: "Health & Safety Investigations",
        icon: "HardHat",
        tagline: "Defence for businesses and individuals facing HSE investigations and prosecutions.",
        summary:
          "It is a stressful time when a business is being investigated in relation to health and safety breaches, which can be regulatory or criminal brought by the Health and Safety Executive. The impact can be devastating both financially and reputationally.",
        covers: [
          "HSE investigations & prosecutions",
          "Workplace fatality investigations",
          "Corporate manslaughter allegations",
          "Improvement & prohibition notices",
        ],
        faqs: [
          {
            q: "The HSE is investigating an incident at our workplace. What should we do?",
            a: "Take advice before any interviews or written responses. The financial and reputational impact of an HSE prosecution can be devastating — early, robust representation protects your position.",
          },
        ],
      },
      {
        slug: "inquests-public-inquiries",
        title: "Inquests & Public Inquiries",
        icon: "Landmark",
        tagline: "Representation at inquests, public inquiries and related proceedings.",
        summary:
          "These are fact-finding investigations into a set of events or deaths. They are specialist areas of the law in which we have a vast amount of experience — including representing a Core Participant in Phase 2 of the Grenfell Tower Inquiry and an Interested Party in the Stephen Port Inquest. Preparation for such matters is crucial and we have the capability and experience to ensure that the vast quantity of evidence is analysed for the best representation for our clients.",
        covers: [
          "Representation of Core Participants in public inquiries",
          "Interested Persons at inquests",
          "Article 2 inquests",
          "Related criminal & regulatory proceedings",
        ],
        faqs: [
          {
            q: "What is the difference between an inquest and a public inquiry?",
            a: "An inquest is a fact-finding investigation into a death; a public inquiry investigates events of public concern. Neither determines criminal liability, but both can have serious consequences — careful preparation and representation is crucial.",
          },
          {
            q: "Have you acted in high-profile inquiries?",
            a: "Yes. Our experience includes representing a Core Participant in Phase 2 of the Grenfell Tower Inquiry and an Interested Party in the Stephen Port Inquest.",
          },
        ],
      },
      {
        slug: "regulatory-body-investigations",
        title: "Regulatory Body Investigations",
        icon: "BadgeCheck",
        tagline: "Defence for professionals facing disciplinary and regulatory proceedings.",
        summary:
          "Our experience includes representing those in the police, healthcare professionals, lawyers and accountants. We are retained by the Police Federation to advise their members regarding disciplinary and regulatory matters as well as any follow on criminal proceedings.",
        covers: [
          "Police officers (retained by the Police Federation)",
          "Healthcare professionals",
          "Lawyers & accountants",
          "Follow-on criminal proceedings",
        ],
        faqs: [
          {
            q: "My professional body has opened an investigation. Could it become criminal?",
            a: "It can. We advise on both the disciplinary process and any follow-on criminal proceedings, ensuring nothing said in one forum prejudices the other.",
          },
        ],
      },
    ],
  },
  {
    slug: "appeals",
    title: "Appeals",
    icon: "RotateCcw",
    tagline:
      "Challenging convictions and sentences — from the Magistrates' Court to the Supreme Court.",
    summary:
      "A conviction or sentence is not necessarily the end of the road. We advise on and conduct appeals against conviction and sentence from both the Magistrates' Court and the Crown Court — and we have taken cases as far as the Court of Appeal and the Supreme Court. Strict time limits apply, so it is vital to seek advice as soon as possible after conviction or sentence.",
    children: [],
    covers: [
      "Appeals against conviction from the Crown Court",
      "Appeals against sentence",
      "Appeals from the Magistrates' Court to the Crown Court",
      "Applications to the Criminal Cases Review Commission (CCRC)",
      "Judicial review of decisions in criminal proceedings",
    ],
    faqs: [
      {
        q: "How long do I have to appeal?",
        a: "From the Crown Court, notice of appeal against conviction or sentence must generally be lodged within 28 days. From the Magistrates' Court to the Crown Court, the limit is 15 business days. Out-of-time applications are possible but need compelling reasons — act immediately.",
      },
      {
        q: "I wasn't represented by Hallinans at trial. Can you still advise on an appeal?",
        a: "Yes. We regularly provide second opinions on conviction and sentence for clients represented elsewhere at trial, and we are frequently trusted with referrals from other law firms.",
      },
      {
        q: "What if my appeal has already been refused?",
        a: "The Criminal Cases Review Commission (CCRC) can refer cases back to the Court of Appeal where there is new evidence or argument. We can advise on whether your case meets the threshold.",
      },
    ],
  },
  {
    slug: "youth-justice",
    title: "Youth Justice",
    icon: "GraduationCap",
    tagline:
      "Specialist, sensitive representation for children and young people in the justice system.",
    summary:
      "When a child or young person is in trouble with the police, the whole family feels it. The youth justice system has its own courts, procedures and sentencing framework — and decisions taken now can shape a young person's future, education and career. We provide sensitive, specialist representation for under-18s from the police station to the Youth Court and beyond, always with the young person's welfare and future at the centre.",
    children: [],
    covers: [
      "Police station representation for under-18s",
      "Youth Court proceedings",
      "Out-of-court disposals — youth cautions & community resolutions",
      "Grave crimes and Crown Court cases involving youths",
      "Advice to parents and guardians",
    ],
    faqs: [
      {
        q: "My child has been arrested. What are their rights?",
        a: "A child must have an appropriate adult present at the police station, and they have the right to free legal advice — always use it. Call our 24 hour line and we will attend or arrange representation immediately.",
      },
      {
        q: "Will a youth caution stay on my child's record?",
        a: "Youth cautions are spent immediately, and for those under 18 at conviction the rehabilitation and filtering periods are significantly shorter than for adults. We will always pursue the outcome that best protects your child's future.",
      },
    ],
  },
];

// Flatten helpers
export const getGroup = (slug) => SERVICE_GROUPS.find((g) => g.slug === slug);
export const getOffence = (groupSlug, slug) => {
  const group = getGroup(groupSlug);
  return group?.children.find((c) => c.slug === slug);
};
export const ALL_OFFENCES = SERVICE_GROUPS.flatMap((g) =>
  g.children.map((c) => ({ ...c, group: g.slug, groupTitle: g.title }))
);

// Legacy slug redirects (old flat /services/:slug structure)
export const LEGACY_SERVICE_MAP = {
  "criminal-defence": "/services/general-crime",
  fraud: "/services/serious-specialist-crime/serious-fraud",
  "motoring-offences": "/services/general-crime/motoring-road-traffic",
  "corporate-crime": "/services/serious-specialist-crime/corporate-crime",
  extradition: "/services/serious-specialist-crime/extradition",
  "inquests-public-inquiries": "/services/regulatory-investigations/inquests-public-inquiries",
  "trading-standards": "/services/regulatory-investigations/trading-standards",
  regulatory: "/services/regulatory-investigations/regulatory-body-investigations",
  "health-safety": "/services/regulatory-investigations/health-safety-investigations",
  "victims-of-crime": "/services",
  "general-advisory": "/services",
};
